<script setup>
import { ref, computed, onMounted } from 'vue'
import { useGlobalStore } from '@stores/global'
import { useEventStore } from '@stores/event'
import Loading from '@components/global/Loading.vue'
import Hero from '@sections/global/Hero.vue'
import ContentFrame from '@components/global/ContentFrame.vue'
import ContentSection from '@components/global/ContentSection.vue'
import Input from '@components/forms/Input.vue'
import Textarea from '@components/forms/Textarea.vue'
import Button from '@components/forms/Button.vue'
import Select from '@components/forms/Select.vue'

const global = useGlobalStore()

const filters = ref({})

const leaderboardItems = computed(() => {
	return global.games.filter((game) => game.slug === filters.value.game)[0]?.leaderboard_items ?? []
})

const filteredItems = computed(() => {
	return leaderboardItems.value.filter((item) => {
		const searchTerm = (filters.value.search || '').toLowerCase()
		const searchMatch = !searchTerm || item.player.username.toLowerCase().includes(searchTerm)
		return searchMatch
	})
})

const clear = () => {
	filters.value = {}
}

onMounted(() => {
	if (global.games.length > 0) {
		filters.value.game = global.games[0].slug
	} else {
		global.getGames().then(() => {
			if (global.games.length > 0) {
				filters.value.game = global.games[0].slug
			}
		})
	}
})
</script>

<template>
	<Loading v-if="global.games?.length === 0" />
	<template v-else>
		<Hero title="Points Ranking" description="See who's at the top of the SNK Esports world." />
		<section>
			<div class="container">
				<div class="relative flex items-center gap-3">
					<!-- <div class="hidden items-center gap-3 sm:flex">
						<Toggle :options="views" v-model="view" />
						<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					</div> -->
					<div class="flex grow flex-col gap-3 md:flex-row">
						<Input class="secondary w-full" v-model="filters.search" placeholder="Search" />
						<Select class="secondary w-full" v-model="filters.game">
							<template v-for="game in global.games" :key="game.id">
								<option :value="game.slug" v-if="game.leaderboard_items.length > 0">{{ game.name }}</option>
							</template>
						</Select>
						<!-- <Select class="secondary w-full" :options="event.regions" v-model="filters.region" placeholder="Region" /> -->
					</div>
					<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					<div class="flex gap-3">
						<Button class="secondary" @click="clear">Clear</Button>
					</div>
				</div>
			</div>
		</section>
		<section class="my-10">
			<div class="container">
				<ContentFrame class="overflow-x-scroll">
					<div class="my-2 px-2 text-sm">★ = Qualified for SWC 2026</div>
					<table>
						<thead>
							<tr>
								<th>Rank</th>
								<th class="min-w-48">Player</th>
								<th>Total</th>
								<th>Legend</th>
								<th class="text-nowrap">Master 1</th>
								<th class="text-nowrap">Master 2</th>
								<th class="text-nowrap">Master 3</th>
								<th>Online</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in filteredItems" v-if="filteredItems.length > 0">
								<td :class="[{ first: item.rank === 1 }, { second: item.rank === 2 }, { third: item.rank === 3 }]">
									<div class="flex items-center gap-3">
										<i v-if="item.rank <= 3" :class="['fas fa-trophy leading-none', { 'text-[#DEBF77]': item.rank === 1 }, { 'text-[#C2C2C2]': item.rank === 2 }, { 'text-[#D39881]': item.rank === 3 }]" />
										#{{ item.rank }}
									</div>
								</td>
								<td class="py-4">
									<div class="flex items-center gap-3 text-nowrap">
										<img :src="item.player.avatar" class="size-10 rounded-full bg-linear-to-t from-snk-light-900 to-white object-cover object-top md:size-16" v-if="item.player.avatar" />
										{{ item.player.username }}
									</div>
								</td>
								<!-- <td>{{ item.player.country.flag }} {{ item.player.country.slug }}</td> -->
								<td>{{ item.total_points.toLocaleString('en-GB') }}</td>
								<td>{{ item.legend.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_1.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_2.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_3.toLocaleString('en-GB') }}</td>
								<td>{{ item.online.toLocaleString('en-GB') }}</td>
							</tr>
							<tr v-else>
								<td colspan="8" class="text-center">There are no players matching your filters.</td>
							</tr>
						</tbody>
					</table>
				</ContentFrame>
			</div>
		</section>
	</template>
</template>

<style scoped>
@reference '../../css/theme.css';

.list-leave-active {
	@apply absolute;
}
.grid-leave-active {
	@apply absolute;
}
</style>
